import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { BannerConfig, subscribeToBannerConfig } from '@/services/bannerService';

interface BannerContextType {
  bannerConfig: BannerConfig;
  loading: boolean;
  dismissedBanners: Set<string>;
  dismissBanner: (bannerId: string) => void;
  isBannerDismissed: (bannerId: string) => boolean;
}

const BannerContext = createContext<BannerContextType | undefined>(undefined);

export const BannerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [bannerConfig, setBannerConfig] = useState<BannerConfig>({});
  const [loading, setLoading] = useState(true);
  const [dismissedBanners, setDismissedBanners] = useState<Set<string>>(new Set());

  // Load dismissed banners from sessionStorage on mount (session-only persistence)
  useEffect(() => {
    const savedDismissed = sessionStorage.getItem('dismissedBanners');
    if (savedDismissed) {
      try {
        const dismissed = JSON.parse(savedDismissed);
        setDismissedBanners(new Set(dismissed));
      } catch (error) {
        // Silent error handling
      }
    }
  }, []);

  // Save dismissed banners to sessionStorage whenever it changes (session-only persistence)
  useEffect(() => {
    sessionStorage.setItem('dismissedBanners', JSON.stringify(Array.from(dismissedBanners)));
  }, [dismissedBanners]);

  useEffect(() => {
    // Subscribe to banner configuration changes
    const unsubscribe = subscribeToBannerConfig((config) => {
      setBannerConfig(config);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  const dismissBanner = (bannerId: string) => {
    setDismissedBanners(prev => new Set([...prev, bannerId]));
  };

  const isBannerDismissed = (bannerId: string) => {
    return dismissedBanners.has(bannerId);
  };

  const value = {
    bannerConfig,
    loading,
    dismissedBanners,
    dismissBanner,
    isBannerDismissed,
  };

  return (
    <BannerContext.Provider value={value}>
      {children}
    </BannerContext.Provider>
  );
};

export const useBanner = (): BannerContextType => {
  const context = useContext(BannerContext);
  if (context === undefined) {
    throw new Error('useBanner must be used within a BannerProvider');
  }
  return context;
}; 